<template>
  <transition name="modal">
    <div v-if="show" class="modal-mask" @click.self="closeModal">
      <div class="modal-wrapper">
        <div class="modal-container">
          <!-- Header -->
          <div class="modal-header">
            <h3 class="text-xl font-bold text-gray-800">
              {{ $t("message.pending_products.export") }}
            </h3>
            <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- From Date -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t("message.pending_products.from_date") }}
              </label>
              <input
                v-model="filters.fromDate"
                type="date"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <!-- To Date -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t("message.pending_products.to_date") }}
              </label>
              <input
                v-model="filters.toDate"
                type="date"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <p class="text-xs text-gray-500 mb-4">
              Leave both dates empty to export every pending product.
            </p>

            <!-- Status -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t("message.pending_products.status") }}
              </label>
              <div class="space-y-2">
                <div class="flex items-center">
                  <input
                    v-model="filters.statusAll"
                    type="checkbox"
                    id="status_all"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    @change="toggleAllStatuses"
                  />
                  <label for="status_all" class="ml-2 text-sm text-gray-700">
                    {{ $t("message.pending_products.all") }}
                  </label>
                </div>
                <div class="ml-4 space-y-2">
                  <div class="flex items-center">
                    <input
                      v-model="filters.statuses"
                      type="checkbox"
                      value="reported"
                      id="status_reported"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      @change="updateStatusAll"
                    />
                    <label
                      for="status_reported"
                      class="ml-2 text-sm text-gray-700"
                    >
                      {{ $t("message.pending_products.reported") }}
                    </label>
                  </div>
                  <div class="flex items-center">
                    <input
                      v-model="filters.statuses"
                      type="checkbox"
                      value="addressed"
                      id="status_addressed"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      @change="updateStatusAll"
                    />
                    <label
                      for="status_addressed"
                      class="ml-2 text-sm text-gray-700"
                    >
                      {{ $t("message.pending_products.addressed") }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div
              v-if="error"
              class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
            >
              {{ error }}
            </div>

            <!-- Status / Download -->
            <div
              v-if="processingExport"
              class="bg-blue-50 border border-blue-200 rounded p-4 mb-4 flex items-center gap-3"
            >
              <BaseMiniSpinner />
              <div>
                <p class="text-sm font-semibold text-blue-900">
                  {{ $t("message.pending_products.exporting") }}
                </p>
                <p class="text-xs text-blue-700 mt-1">
                  {{ exportResponse.fileName || exportResponse.status }}
                </p>
              </div>
            </div>

            <div
              v-if="readyExport"
              class="bg-blue-50 border border-blue-200 rounded p-4 mb-4"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <p class="text-sm font-semibold text-blue-900">
                    {{ $t("message.pending_products.export_completed") }}
                  </p>
                  <p class="text-xs text-blue-700 mt-1">
                    {{ exportResponse.fileName }}
                  </p>
                </div>
                <button
                  @click="downloadExportedFile"
                  :disabled="downloading || !exportResponse.s3Path"
                  class="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    ></path>
                  </svg>
                  {{
                    downloading
                      ? $t("message.pending_products.exporting")
                      : $t("message.pending_products.download")
                  }}
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button
              @click="closeModal"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              :disabled="loading || downloading"
            >
              {{ $t("message.request_restock_modal.cancel") }}
            </button>
            <button
              v-if="!exportResponse || exportResponse.status !== 'created'"
              @click="exportPendingProducts"
              :disabled="loading || exportResponse?.status === 'in_process'"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <span v-if="loading">
                <svg
                  class="animate-spin h-5 w-5 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {{ $t("message.pending_products.exporting") }}
              </span>
              <span v-else-if="exportResponse?.status === 'in_process'">
                {{ $t("message.pending_products.exporting") }}
              </span>
              <span v-else>
                {{ $t("message.pending_products.export") }}
              </span>
            </button>
            <button
              v-else
              @click="closeModal"
              class="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              {{ $t("message.request_restock_modal.close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  getReportFileInfo,
  requestReport,
  signAndDownloadFile
} from "@/api/store/ReportGeneratorService";
import BaseMiniSpinner from "@/components/global/_base-mini-spinner.vue";

export default {
  name: "ExportPendingProductsModal",
  components: {
    BaseMiniSpinner
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    departmentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      error: null,
      exportResponse: null,
      pollTimer: null,
      filters: {
        fromDate: "",
        toDate: "",
        statusAll: true,
        statuses: []
      }
    };
  },
  computed: {
    processingExport() {
      return this.exportResponse?.status === "in_process";
    },
    readyExport() {
      return this.exportResponse?.status === "created";
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeDates();
        this.error = null;
        this.exportResponse = null;
      } else {
        this.stopPolling();
      }
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    normalizeReportPayload(payload = {}) {
      return {
        id: payload.reportId || payload.id || null,
        status: payload.status || null,
        fileName: payload.fileName || payload.file_name || null,
        s3Path: payload.s3Path || payload.s3_path || null,
        s3Url: payload.s3Url || payload.s3_url || null,
        results: payload.results || null,
        raw: payload
      };
    },
    initializeDates() {
      this.filters.fromDate = "";
      this.filters.toDate = "";
      this.filters.statusAll = true;
      this.filters.statuses = [];
    },
    toggleAllStatuses() {
      if (this.filters.statusAll) {
        this.filters.statuses = [];
      } else {
        this.filters.statuses = ["reported", "addressed"];
      }
    },
    updateStatusAll() {
      if (this.filters.statuses.length === 0) {
        this.filters.statusAll = true;
      } else if (this.filters.statuses.length === 2) {
        this.filters.statusAll = true;
      } else {
        this.filters.statusAll = false;
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    startPolling(reportId) {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        try {
          const info = this.normalizeReportPayload(
            await getReportFileInfo(reportId)
          );
          if (info.status === "in_process") {
            return;
          }

          this.stopPolling();
          this.exportResponse = info;

          if (info.status === "created") {
            this.error = null;
          } else {
            this.error =
              info.results?.message ||
              this.$t("message.pending_products.failed_to_export");
          }
        } catch (err) {
          console.error("Error polling pending products export:", err);
        }
      }, 3000);
    },
    async exportPendingProducts() {
      this.loading = true;
      this.error = null;

      try {
        const reportData = {
          fileType: "lost_sales_export",
          otherOptions: {
            department_id: this.departmentId,
            statuses:
              this.filters.statusAll || this.filters.statuses.length === 0
                ? ["reported", "addressed"]
                : this.filters.statuses
          }
        };

        // Backend currently requires both date fields for this report and fails on NULL.
        // Keep date inputs optional in UI by sending a full-range fallback when empty.
        reportData.otherOptions.from_date =
          this.filters.fromDate || "1900-01-01";
        reportData.otherOptions.to_date = this.filters.toDate || "2999-12-31";

        const response = await requestReport(reportData);
        const normalizedResponse = this.normalizeReportPayload(response);

        if (
          normalizedResponse.id &&
          normalizedResponse.status === "in_process"
        ) {
          this.exportResponse = normalizedResponse;
          this.startPolling(normalizedResponse.id);
        } else if (normalizedResponse.status === "created") {
          this.exportResponse = normalizedResponse;
        } else {
          this.exportResponse = normalizedResponse || null;
          this.error =
            response?.message ||
            normalizedResponse?.results?.message ||
            this.$t("message.pending_products.failed_to_export");
        }
      } catch (err) {
        this.error =
          err.response?.data?.message ||
          err.message ||
          "An error occurred while exporting";
        console.error("Error exporting pending products:", err);
      } finally {
        this.loading = false;
      }
    },
    async downloadExportedFile() {
      if (!this.exportResponse?.s3Path) {
        return;
      }

      this.downloading = true;
      try {
        const downloaded = await signAndDownloadFile(
          this.exportResponse.s3Path,
          this.exportResponse.s3Url || this.exportResponse.s3Route || null
        );

        if (!downloaded) {
          this.error = this.$t("message.pending_products.failed_to_export");
        }
      } finally {
        this.downloading = false;
      }
    },
    closeModal() {
      if (!this.loading && !this.downloading) {
        this.stopPolling();
        this.$emit("close");
      }
    }
  }
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(90vh - 150px);
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}
</style>
